import React, { useState, useEffect } from "react";
import { api, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { AlertTriangle, Ban, RotateCcw } from "lucide-react";
import { toast } from "sonner";

/**
 * Diálogo para marcar una Nota de Crédito como ANULADA en SRI.
 * ITER188: revierte automáticamente stock, CxC y ajuste de comisión.
 */
export default function MarcarNcAnuladaSriDialog({ nc, onClose, onSaved }) {
  const [motivo, setMotivo] = useState("");
  const [fechaPortal, setFechaPortal] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (nc) {
      setMotivo("");
      setFechaPortal(new Date().toISOString().slice(0, 10));
    }
  }, [nc]);

  if (!nc) return null;

  const submit = async () => {
    if (motivo.trim().length < 5) {
      toast.error("Debes indicar un motivo (mínimo 5 caracteres)");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post(`/invoicing/notas-credito/${nc.id}/marcar-anulada-sri`, {
        motivo: motivo.trim(),
        fecha_anulacion_sri: fechaPortal || null,
      });
      const rev = data?.reversiones || {};
      const parts = [];
      if ((rev.stock || []).length > 0) parts.push(`${rev.stock.length} lote(s) revertido(s)`);
      if (rev.cxc) parts.push(`CxC saldo: $${(rev.cxc.nuevo_saldo || 0).toFixed(2)}`);
      if (rev.comision) parts.push(`Comisión +$${(rev.comision.reversado || 0).toFixed(2)}`);
      toast.success(`NC ${nc.numero} anulada. ${parts.join(" · ") || "Sin reversiones."}`);
      onSaved && onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!nc} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="marcar-nc-anulada-sri-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#b53a25]">
            <Ban size={18} /> Anular Nota de Crédito en SRI
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 text-sm">
          <div className="rounded-md border border-[#f5c477] bg-[#fef7ec] p-3 text-[#7a4000] flex gap-2">
            <AlertTriangle size={16} className="shrink-0 mt-0.5" />
            <div className="text-xs">
              <div className="font-semibold mb-1">Al anular esta NC se revertirán automáticamente:</div>
              <ul className="list-disc pl-4 space-y-0.5">
                <li>Stock reingresado por la NC (saldrá de nuevo del lote)</li>
                <li>Efecto sobre la CxC de la factura origen (aumentará el saldo)</li>
                <li>Descuento de comisión asociado (se sumará de vuelta al vendedor)</li>
              </ul>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2 text-xs">
            <div><span className="text-[#5c7368]">NC</span><div className="font-mono font-semibold text-[#15241d]">{nc.numero}</div></div>
            <div><span className="text-[#5c7368]">Total</span><div className="font-semibold text-[#15241d]">${(nc.total || 0).toFixed(2)}</div></div>
            <div><span className="text-[#5c7368]">Factura origen</span><div className="font-mono text-[#15241d]">{nc.factura_origen_numero || "—"}</div></div>
            <div><span className="text-[#5c7368]">Estado actual</span><div className="uppercase font-semibold">{nc.estado_sri || "—"}</div></div>
          </div>

          <div>
            <Label>Fecha de anulación en SRI</Label>
            <Input type="date" value={fechaPortal} onChange={(e) => setFechaPortal(e.target.value)} data-testid="anular-nc-sri-fecha" />
          </div>

          <div>
            <Label>Motivo <span className="text-[#b53a25]">*</span></Label>
            <Textarea value={motivo} onChange={(e) => setMotivo(e.target.value)}
              placeholder="Ej: Anulada junto con factura origen por error en productos."
              rows={3} data-testid="anular-nc-sri-motivo" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="anular-nc-sri-cancel-btn">Cancelar</Button>
          <Button
            onClick={submit}
            disabled={saving || motivo.trim().length < 5}
            className="bg-[#b53a25] hover:bg-[#8f2a18] text-white"
            data-testid="anular-nc-sri-confirm-btn"
          >
            <RotateCcw size={14} className="mr-1" />
            {saving ? "Revirtiendo…" : "Anular y revertir todo"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
